/**
 * JMAP transport.
 *
 * Every request is same-origin: nginx serves this bundle and proxies the API
 * paths to hydramaild, so there is no base URL to configure and no CORS
 * preflight.
 *
 * The credential is usually not here at all. A password login leaves an
 * HttpOnly cookie the browser attaches by itself, which is why every request
 * below says `credentials: "same-origin"` and most of them send no
 * `Authorization` header. The device-token path is kept for the other clients
 * the daemon serves -- scripts, curl, anything not this page -- and for anyone
 * who pasted a token before there was a login screen.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import { request as fetch, saveFile } from "@transport";
import type { Email, Envelope, Mailbox, SearchSnippet, Session } from "./types";
import { authState } from "./auth";
import { authHeaders, deviceToken, looksLikeToken } from "./settings";

// Re-exported: `ui/Settings.tsx` and this module's own tests have always taken
// it from here, and moving the definition is not a reason to move the import.
export { looksLikeToken };

const CORE = "urn:ietf:params:jmap:core";
const MAIL = "urn:ietf:params:jmap:mail";
/**
 * Requested only by the calls that need it, rather than added to every batch.
 *
 * `using` is a claim about what the request contains, and a server is entitled
 * to refuse a capability it does not implement -- with an `unknownCapability`
 * that rejects the *whole* request, not the one call. So a client that always
 * asked for submission would stop being able to read mail the moment it met a
 * daemon built without it.
 */
export const SUBMISSION = "urn:ietf:params:jmap:submission";

/** Properties worth fetching for a list row. Deliberately not the body. */
export const LIST_PROPERTIES = [
  "id",
  "threadId",
  "mailboxIds",
  "keywords",
  "size",
  "receivedAt",
  "sentAt",
  "subject",
  "preview",
  "hasAttachment",
  "from",
  "to",
  // Which of the inbox's two tabs the message belongs to. On the list rows and
  // not just the open message, because the reader has to be able to see that a
  // row they are looking at is one they demoted, and because flagging one has
  // to move it between tabs without a round trip.
  "lowPriority",
];

export class ApiError extends Error {
  readonly status: number;
  /** JMAP method-level error type, when the failure was inside a batch. */
  readonly kind: string | undefined;

  constructor(message: string, status: number, kind?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.kind = kind;
  }
}

export type Invocation = [name: string, args: Record<string, unknown>, callId: string];

/**
 * How far this browser's clock is ahead of the daemon's, in milliseconds.
 *
 * It exists for one caller. The undo window is expressed to the server as
 * `sendAt`, an *absolute* UTC time, so a device whose clock is ten minutes slow
 * asks for a release time ten minutes in the past and gets no undo window at
 * all -- silently, because the request is well formed and the server does
 * exactly what it was told. A clock that is fast is the mirror image: the
 * message sits in the outbox long after the countdown has run out.
 *
 * Reading the `Date` header off responses we were making anyway costs nothing
 * and removes the whole class of failure. It is accurate to a second or two at
 * best -- one-second resolution, plus however long the response was in flight
 * -- which is the right precision for the job: this is here to catch a clock
 * that is minutes or hours out, not to shave a tick off a countdown.
 *
 * Not a signal. Nothing renders it, and a value that changed on every request
 * would invalidate every computed in the app several times a minute.
 */
let clockSkewMs = 0;

/** Now, on the daemon's clock, as best this browser can tell. */
export function serverNow(): number {
  return Date.now() - clockSkewMs;
}

function noteServerClock(res: Response): void {
  const header = res.headers.get("date");
  if (!header) return;
  const stamped = Date.parse(header);
  if (Number.isNaN(stamped)) return;
  clockSkewMs = Date.now() - stamped;
}

interface ApiResponse {
  methodResponses: Invocation[];
  sessionState: string;
}

async function failure(res: Response): Promise<never> {
  let detail = res.statusText;
  try {
    const body = await res.json();
    detail = body.detail ?? body.error ?? body.title ?? detail;
  } catch {
    // A proxy error page is HTML, not JSON; the status line is the message.
  }
  if (res.status === 401) {
    // Tell the app, not just the caller. This used to throw a string and stop,
    // which meant a revoked or expired credential rendered as a working mail UI
    // with a red banner and no route back to a login screen -- the client had
    // no notion of being signed out at all.
    authState.value = "out";
    throw new ApiError("Your session has ended. Sign in again.", 401);
  }
  throw new ApiError(detail, res.status);
}

export async function getSession(token = deviceToken.value): Promise<Session> {
  const res = await fetch("/.well-known/jmap", {
    credentials: "same-origin",
    headers: authHeaders(token),
  });
  if (!res.ok) await failure(res);
  noteServerClock(res);
  return (await res.json()) as Session;
}

/**
 * Run a batch. One HTTP request per user action, however many accounts it
 * touches -- a unified inbox over five accounts is five query/get pairs in a
 * single round trip, which is the whole reason JMAP batches at all.
 */
export async function call(
  methodCalls: Invocation[],
  extraCapabilities: string[] = [],
): Promise<Map<string, Record<string, unknown>>> {
  const res = await fetch("/jmap/api", {
    method: "POST",
    credentials: "same-origin",
    headers: authHeaders(deviceToken.value),
    body: JSON.stringify({ using: [CORE, MAIL, ...extraCapabilities], methodCalls }),
  });
  if (!res.ok) await failure(res);
  noteServerClock(res);
  const body = (await res.json()) as ApiResponse;

  const out = new Map<string, Record<string, unknown>>();
  for (const [name, args, callId] of body.methodResponses) {
    if (name === "error") {
      // Recorded rather than thrown: in a unified query one dead account must
      // not blank the other four. The caller decides what a hole means.
      out.set(callId, { __error: args });
      continue;
    }
    out.set(callId, args);
  }
  return out;
}

function unwrap<T>(results: Map<string, Record<string, unknown>>, callId: string): T {
  const value = results.get(callId);
  if (!value) throw new ApiError(`the server skipped call ${callId}`, 500);
  const err = value["__error"] as { type?: string; description?: string } | undefined;
  if (err) throw new ApiError(err.description ?? err.type ?? "method failed", 200, err.type);
  return value as T;
}

/**
 * One account's folders, and the state they were read at.
 *
 * The state is what makes the next update cheap: it is the argument
 * `Mailbox/changes` takes, and without it the only way to learn that a counter
 * moved is to fetch every mailbox again. `undefined` when the account failed,
 * which is the case that must not be treated as "state zero" -- asking for
 * changes since a state we never had is how a client silently misses
 * everything between.
 */
export interface MailboxPage {
  list: Mailbox[];
  state: string | undefined;
}

export async function getMailboxes(accountIds: string[]): Promise<Map<string, MailboxPage>> {
  if (accountIds.length === 0) return new Map();
  const results = await call(
    accountIds.map((id, i): Invocation => ["Mailbox/get", { accountId: id, ids: null }, `m${i}`]),
  );
  const out = new Map<string, MailboxPage>();
  accountIds.forEach((id, i) => {
    try {
      const got = unwrap<{ list: Mailbox[]; state?: string }>(results, `m${i}`);
      out.set(id, { list: got.list, state: got.state });
    } catch {
      out.set(id, { list: [], state: undefined });
    }
  });
  return out;
}

export interface QuerySpec {
  accountId: string;
  /** Omitted for a search across every mailbox in the account. */
  mailboxId?: string | undefined;
  text?: string | undefined;
  /**
   * Extra `FilterCondition` fields to AND in: `notKeyword`, `hasAttachment`,
   * `before` and the rest. Built by `lib/filters.ts`, which owns what they
   * mean; this layer only has to put them on the wire.
   *
   * A bag rather than named arguments because every one of them is a condition
   * the daemon already implements, and threading each through as its own
   * optional field would mean editing this signature every time the bar grows
   * a chip.
   */
  conditions?: Record<string, unknown> | undefined;
  /**
   * Leave unfinished messages out of the results.
   *
   * Set for a search and nothing else. A search result is something you open
   * to *read*, and a draft that surfaces there opens the composer instead --
   * a jarring answer, and one Gmail and Thunderbird both avoid by excluding
   * drafts from search. The Drafts folder itself remains the place they are
   * found, which is why this is not simply part of every query.
   */
  excludeDrafts?: boolean | undefined;
  position: number;
  limit: number;
}

/*
 * The bucket rides in `conditions` like everything else rather than as a field
 * of its own -- `store.specFor` puts `lowPriority` there when the view has two
 * tabs and leaves it out when it does not. That is what makes "no bucket" mean
 * *both* buckets at the transport layer, which is the right meaning for
 * Archive, for Sent and for a search.
 */

export interface QueryPage {
  accountId: string;
  emails: Email[];
  total: number | undefined;
  /**
   * What the server called this result set's state.
   *
   * Carried so the next update can be a delta: it is the `sinceQueryState`
   * `Email/queryChanges` takes, and it is the only honest one -- the state a
   * *different* call reported may be either side of this query's own reads.
   * `undefined` when the account failed, which `lib/delta.ts` treats as "no
   * delta is possible here", never as zero.
   */
  queryState: string | undefined;
  /** Set when this one account failed while others succeeded. */
  error: string | undefined;
}

/**
 * The scope, the search and the filters as one JMAP filter.
 *
 * They are ANDed, and the AND is the point: `hm-store` compiles the whole tree
 * into a single indexed statement, so "unread, with an attachment, in this
 * folder, mentioning invoice" is one query rather than four result sets the
 * client would have to intersect -- and intersecting paged result sets by hand
 * is not something a client can do correctly at all.
 *
 * The conditions are flattened into one condition object where they can be,
 * rather than nested one per clause. JMAP defines the fields of a single
 * condition as already ANDed together, so the flat form means the same thing
 * and is what the daemon's own tests read like.
 */
// Exported for its tests: the operator-tree fallback below is wire shape the
// fake server does not interpret, so the only place to pin it is the filter
// itself.
export function filterFor(spec: QuerySpec): Record<string, unknown> | undefined {
  const flat: Record<string, unknown> = { ...(spec.conditions ?? {}) };
  if (spec.mailboxId) flat["inMailbox"] = spec.mailboxId;
  if (spec.text) flat["text"] = spec.text;
  if (spec.excludeDrafts) {
    // `notKeyword` holds one keyword, and the Unread chip already spends it
    // on `$seen`. When both are wanted the flat form cannot say so, and an
    // operator tree can -- the daemon compiles either into one statement.
    if (flat["notKeyword"] && flat["notKeyword"] !== "$draft") {
      return { operator: "AND", conditions: [flat, { notKeyword: "$draft" }] };
    }
    flat["notKeyword"] = "$draft";
  }
  return Object.keys(flat).length === 0 ? undefined : flat;
}

/**
 * Query several accounts at once and fetch the rows in the same round trip.
 *
 * The `#ids` back-reference is what makes that possible: the `Email/get` names
 * the query's own result rather than a list of ids the client would otherwise
 * have to wait for.
 */
export async function queryPages(specs: QuerySpec[]): Promise<QueryPage[]> {
  if (specs.length === 0) return [];
  const calls: Invocation[] = [];
  specs.forEach((spec, i) => {
    const filter = filterFor(spec);
    calls.push([
      "Email/query",
      {
        accountId: spec.accountId,
        ...(filter ? { filter } : {}),
        sort: [{ property: "receivedAt", isAscending: false }],
        position: spec.position,
        limit: spec.limit,
        calculateTotal: true,
      },
      `q${i}`,
    ]);
    calls.push([
      "Email/get",
      {
        accountId: spec.accountId,
        "#ids": { resultOf: `q${i}`, name: "Email/query", path: "/ids" },
        properties: LIST_PROPERTIES,
      },
      `g${i}`,
    ]);
  });

  const results = await call(calls);
  return specs.map((spec, i): QueryPage => {
    try {
      const query = unwrap<{ total?: number; queryState?: string }>(results, `q${i}`);
      const got = unwrap<{ list: Email[] }>(results, `g${i}`);
      return {
        accountId: spec.accountId,
        emails: got.list,
        total: query.total,
        queryState: query.queryState,
        error: undefined,
      };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return {
        accountId: spec.accountId,
        emails: [],
        total: undefined,
        queryState: undefined,
        error: message,
      };
    }
  });
}

/**
 * How many messages to name in one `Email/get` when the session will not say.
 *
 * Deliberately far below what any daemon advertises. The two ways to be wrong
 * here are not symmetrical: guess *high* and the server refuses the call
 * outright rather than truncating it, so the view shows nothing at all; guess
 * *low* and the request carries a few more invocations inside the same batch,
 * which is one round trip either way. So the fallback fails toward the harmless
 * side.
 *
 * It is only ever reached if the core capability is missing or malformed.
 * RFC 8620 requires `maxObjectsInGet` in it, so this is defence against a
 * server that is already wrong rather than a case anyone expects.
 */
const FALLBACK_MAX_GET = 100;

/**
 * What this session says one `Email/get` may name.
 *
 * Read rather than assumed. This used to be `const MAX_GET = 500` beside a
 * comment observing that the session advertises the number -- true, and 500 is
 * this daemon's *default*, which is a configuration value somebody can lower.
 * Two sources of truth for one number, agreeing today by coincidence, is the
 * shape of bug this project keeps meeting: nothing fails until a deployment
 * changes the limit, and then every call is refused.
 */
function maxObjectsInGet(session: Session): number {
  const core = session.capabilities[CORE] as { maxObjectsInGet?: unknown } | undefined;
  const advertised = core?.maxObjectsInGet;
  return typeof advertised === "number" && Number.isInteger(advertised) && advertised > 0
    ? advertised
    : FALLBACK_MAX_GET;
}

/**
 * Fetch a named set of messages, list-shaped.
 *
 * For the one view that knows which messages it wants before it asks: the
 * kept-offline list, whose membership comes from this device's replica rather
 * than from any query the daemon could answer. See `store.fetchSaved`.
 *
 * Chunked because the ids come from somewhere with no bound on how many there
 * may be, and one batch either way -- several `Email/get`s in a single request
 * cost one round trip, so the split is invisible above this line.
 *
 * Ids the server does not know are simply absent from the result. That is the
 * right shape for the caller: a pin can outlive the message it was made on, and
 * the honest answer to "show me what is kept" is the mail that is still there
 * rather than a refusal naming the one that is not.
 */
export async function getEmails(
  session: Session,
  accountId: string,
  ids: string[],
): Promise<Email[]> {
  if (ids.length === 0) return [];
  const limit = maxObjectsInGet(session);
  const chunks: string[][] = [];
  for (let at = 0; at < ids.length; at += limit) chunks.push(ids.slice(at, at + limit));
  const results = await call(
    chunks.map((chunk, i): Invocation => [
      "Email/get",
      { accountId, ids: chunk, properties: LIST_PROPERTIES },
      `b${i}`,
    ]),
  );
  const out: Email[] = [];
  // `unwrap` throws on a failed call, and it is allowed to: unlike a unified
  // query, these chunks are one account's answer split up for the wire, so half
  // of it is not a partial view worth showing -- it is a list missing messages
  // this device has promised to be holding.
  chunks.forEach((_, i) => out.push(...unwrap<{ list: Email[] }>(results, `b${i}`).list));
  return out;
}

// ---- what changed, rather than what there is -----------------------------------

/**
 * How many log rows the daemon may consult for one delta.
 *
 * Its ceiling, deliberately, and not a number chosen for this client. Ask for
 * less and a busy moment -- a sync run landing four hundred messages between
 * two events -- comes back `cannotCalculateChanges`, and the fallback for that
 * is the full reload this whole path exists to avoid. The work it bounds is a
 * scan of an indexed log, and the answer is collapsed before it is sent, so a
 * large ceiling costs a large *response* only when a large number of distinct
 * messages really did change. See `DEFAULT_MAX_CHANGES` and `MAX_CHANGES_LIMIT`
 * in `crates/hm-jmap/src/changes.rs`.
 */
const MAX_CHANGES = 4096;

/** One account's list, and the state it was last read at. */
export interface QueryProbe {
  /**
   * The query the list on screen came from.
   *
   * The filter and the sort are taken from it and must be the ones actually in
   * force: the server answers "what enters and leaves *this* query", so a
   * probe carrying yesterday's filter would answer about a list nobody is
   * looking at. Position and limit are ignored -- a delta has no page.
   */
  spec: QuerySpec;
  sinceQueryState: string;
  /**
   * The oldest message this account has shown, if it has more to show.
   *
   * Insertions past it are outside the window the reader has paged into, and
   * the server drops them rather than sending an id the client would only
   * discard. Omitted once an account is exhausted, because then the list *is*
   * the whole result and anything joining it is on screen.
   */
  upToId?: string | undefined;
}

/**
 * What entered and left one account's list.
 *
 * `removed` is every message that changed at all, not only those that left:
 * the daemon expresses a message that merely moved as a removal plus a
 * re-insertion, and reporting a change it cannot cheaply attribute is the
 * conservative half of that. So `removed` is the right thing to intersect with
 * what the client is holding, and `added` is the right thing to ask whether
 * anything now belongs on screen.
 */
export interface QueryDelta {
  accountId: string;
  added: string[];
  removed: string[];
  newQueryState: string | undefined;
  /**
   * The daemon declined to answer.
   *
   * Expected, not exceptional: the change log is pruned, so a client whose
   * state predates the floor is told to ask again from scratch rather than
   * given a delta that would silently leave deleted mail on screen forever.
   */
  cannotCalculate: boolean;
  error: string | undefined;
}

/** What changed about one account's folders, with the changed ones attached. */
export interface MailboxDelta {
  accountId: string;
  created: string[];
  updated: string[];
  destroyed: string[];
  hasMoreChanges: boolean;
  newState: string | undefined;
  /**
   * The updated mailboxes themselves, fetched in the same round trip.
   *
   * A result reference rather than a second request: the whole point of asking
   * what changed is to be cheap, and paying a round trip to turn four ids into
   * four objects would give most of that back. Only the accounts that own the
   * ids answer with anything -- `Mailbox/get` puts another account's id in
   * `notFound` -- so asking every account for every changed id costs a few
   * bytes of request and returns exactly the right rows.
   */
  list: Mailbox[];
  cannotCalculate: boolean;
  error: string | undefined;
}

function deltaFailure(e: unknown): { cannotCalculate: boolean; error: string } {
  const kind = e instanceof ApiError ? e.kind : undefined;
  return {
    cannotCalculate: kind === "cannotCalculateChanges",
    error: e instanceof Error ? e.message : String(e),
  };
}

/**
 * Ask what changed, in one request.
 *
 * The list question and the folder question travel together because they are
 * one wake-up: the daemon polls a single shared counter, so an event says only
 * that *something* moved, and the two things this client would want to know
 * about it should not cost two round trips.
 *
 * `Email/queryChanges` is used for the list rather than `Email/changes`, and
 * the difference is the whole reason this is cheap. `Email/changes` returns
 * ids; deciding whether an id belongs in the view means knowing its mailboxes
 * and its keywords, so a client using it would have to fetch every changed
 * message to find out that none of them was in the view -- which during a
 * backfill is precisely the fetch being avoided. `queryChanges` asks the
 * server, which already has the filter compiled, and gets back a decision.
 */
export async function pollChanges(
  queries: QueryProbe[],
  mailboxes: { accountId: string; sinceState: string }[],
): Promise<{ queries: QueryDelta[]; mailboxes: MailboxDelta[] }> {
  const calls: Invocation[] = [];

  queries.forEach((probe, i) => {
    const filter = filterFor(probe.spec);
    calls.push([
      "Email/queryChanges",
      {
        accountId: probe.spec.accountId,
        ...(filter ? { filter } : {}),
        sort: [{ property: "receivedAt", isAscending: false }],
        sinceQueryState: probe.sinceQueryState,
        maxChanges: MAX_CHANGES,
        ...(probe.upToId ? { upToId: probe.upToId } : {}),
      },
      `qc${i}`,
    ]);
  });

  mailboxes.forEach((probe, i) => {
    calls.push([
      "Mailbox/changes",
      { accountId: probe.accountId, sinceState: probe.sinceState, maxChanges: MAX_CHANGES },
      `mc${i}`,
    ]);
    calls.push([
      "Mailbox/get",
      {
        accountId: probe.accountId,
        "#ids": { resultOf: `mc${i}`, name: "Mailbox/changes", path: "/updated" },
      },
      `mg${i}`,
    ]);
  });

  if (calls.length === 0) return { queries: [], mailboxes: [] };
  const results = await call(calls);

  return {
    queries: queries.map((probe, i): QueryDelta => {
      const accountId = probe.spec.accountId;
      try {
        const got = unwrap<{
          added?: { id: string; index: number }[];
          removed?: string[];
          newQueryState?: string;
        }>(results, `qc${i}`);
        return {
          accountId,
          added: (got.added ?? []).map((a) => a.id),
          removed: got.removed ?? [],
          newQueryState: got.newQueryState,
          cannotCalculate: false,
          error: undefined,
        };
      } catch (e) {
        return {
          accountId,
          added: [],
          removed: [],
          newQueryState: undefined,
          ...deltaFailure(e),
        };
      }
    }),
    mailboxes: mailboxes.map((probe, i): MailboxDelta => {
      try {
        const got = unwrap<{
          created?: string[];
          updated?: string[];
          destroyed?: string[];
          hasMoreChanges?: boolean;
          newState?: string;
        }>(results, `mc${i}`);
        // The `Mailbox/get` beside it is allowed to fail on its own -- an
        // account with nothing updated still answers, but a daemon that
        // refused the reference should not lose the delta that came with it.
        let list: Mailbox[] = [];
        try {
          list = unwrap<{ list: Mailbox[] }>(results, `mg${i}`).list;
        } catch {
          list = [];
        }
        return {
          accountId: probe.accountId,
          created: got.created ?? [],
          updated: got.updated ?? [],
          destroyed: got.destroyed ?? [],
          hasMoreChanges: got.hasMoreChanges ?? false,
          newState: got.newState,
          list,
          cannotCalculate: false,
          error: undefined,
        };
      } catch (e) {
        return {
          accountId: probe.accountId,
          created: [],
          updated: [],
          destroyed: [],
          hasMoreChanges: false,
          newState: undefined,
          list: [],
          ...deltaFailure(e),
        };
      }
    }),
  };
}

/** The full message, bodies included, for the reading pane. */
export async function getEmail(accountId: string, id: string): Promise<Envelope> {
  const results = await call([
    [
      "Email/get",
      {
        accountId,
        ids: [id],
        properties: [
          ...LIST_PROPERTIES,
          "cc",
          "bcc",
          "replyTo",
          "messageId",
          "inReplyTo",
          "references",
          "textBody",
          "htmlBody",
          "attachments",
          "bodyValues",
        ],
        fetchTextBodyValues: true,
        fetchHTMLBodyValues: true,
      },
      "e0",
    ],
  ]);
  const list = unwrap<{ list: Email[] }>(results, "e0").list;
  const email = list[0];
  if (!email) throw new ApiError("that message is no longer on the server", 404);
  return { accountId, email };
}

/** Highlighted fragments for search results. Best effort: no snippet is fine. */
export async function getSnippets(
  accountId: string,
  text: string,
  ids: string[],
): Promise<Map<string, SearchSnippet>> {
  if (ids.length === 0) return new Map();
  try {
    const results = await call([
      ["SearchSnippet/get", { accountId, filter: { text }, emailIds: ids }, "s0"],
    ]);
    const list = unwrap<{ list: SearchSnippet[] }>(results, "s0").list;
    return new Map(list.map((s) => [s.emailId, s]));
  } catch {
    return new Map();
  }
}

function blobUrl(session: Session, accountId: string, part: BlobRef): string {
  return session.downloadUrl
    .replace("{accountId}", encodeURIComponent(accountId))
    .replace("{blobId}", encodeURIComponent(part.blobId))
    .replace("{name}", encodeURIComponent(part.name || "attachment"))
    .replace("{type}", encodeURIComponent(part.type || "application/octet-stream"));
}

export interface BlobRef {
  blobId: string;
  name: string;
  type: string;
}

/**
 * Save an attachment.
 *
 * Fetched rather than linked, and the reason is the credential: a cookie session
 * would survive a plain `<a href>` because the browser attaches it itself, but a
 * device token would not -- HydraMail authenticates this endpoint by header and
 * never by `?access_token=`, and a browser cannot put a header on a link. One
 * code path that works for both beats two that differ by which credential
 * happens to be in use. There is no query-string fallback on purpose: a token in
 * a URL turns up in proxy logs and in the referrer of anything loaded next.
 *
 * That reasoning is why the *fetch* stays here, shared. Only the last step --
 * putting bytes somewhere a person can find them -- is a thing a browser and a
 * desktop app do differently, and that step is the transport's.
 */
export async function saveAttachment(
  session: Session,
  accountId: string,
  part: BlobRef,
): Promise<void> {
  await saveFile(await fetchAttachment(session, accountId, part), part.name);
}

/**
 * The bytes of an attachment, for looking at rather than keeping.
 *
 * Split out of {@link saveAttachment} so that viewing and saving are one
 * fetch with two endings, rather than two fetches that could disagree about
 * the credential or about which blob they mean. On the desktop this usually
 * touches no network at all: an attachment's blob id names the message it
 * lives in, and the transport reads the part straight out of the copy on disk.
 */
export async function fetchAttachment(
  session: Session,
  accountId: string,
  part: BlobRef,
): Promise<Blob> {
  const res = await fetch(blobUrl(session, accountId, part), {
    credentials: "same-origin",
    headers: authHeaders(deviceToken.value),
  });
  if (!res.ok) await failure(res);
  // The daemon labels the download; a locally-served part answers without a
  // content type, so the part's own is the one to trust either way.
  return new Blob([await res.arrayBuffer()], {
    type: part.type || "application/octet-stream",
  });
}
